import { useTranslations } from "next-intl";
import { useParams, useRouter } from "next/navigation";
import { GA4_PRODUCT_TYPE, trackGa4Event } from "@/analytics/ga4";
import { useToast } from "@/components/feedback";
import { useStepTransition } from "@/components/transitions/transition-context";
import { onTaskSubmit } from "@/core/flow/submit-task";
import {
	LOGIN_STATUS_QUERY,
	LOGIN_STATUS_SESSION_EXPIRED,
} from "@/features/login/login-status";
import { ProcessType, TaskType } from "@/types";

export const useSubmitTask = <T>({
	taskId,
	processType,
	taskType,
}: {
	taskId: string;
	processType: ProcessType;
	taskType: TaskType;
}) => {
	const { flowId } = useParams<{ flowId: string }>();
	const router = useRouter();
	const t = useTranslations("common.feedback");
	const { showToast } = useToast();
	const { runTransition } = useStepTransition();

	return (body: T, onSuccess?: () => void) =>
		runTransition(async () => {
			const result = await onTaskSubmit<T>({
				taskId,
				taskType,
				body,
				flowId,
				processType,
			});

			if (result.succeeded) {
				onSuccess?.();
				router.replace(`/${flowId}`);
				return true;
			}

			if (result.reason === "unauthenticated") {
				router.replace(
					`/${flowId}/login?${LOGIN_STATUS_QUERY}=${LOGIN_STATUS_SESSION_EXPIRED}`
				);
				return true;
			}

			trackGa4Event("signing_error", {
				product_type: GA4_PRODUCT_TYPE[processType],
				error_category: "unknown",
			});
			showToast({ severity: "error", message: t("submitFailed") });
			return false;
		});
};
